import { useCallback, useState, RefObject } from 'react';
import styles from './StyleConfigTab.module.css';
import WebchatWidget from '../../../components/WebchatWidget';
import { WIDGET_LANGUAGE_DEFAULTS } from '../../../shared/config';

const COLORS = ['#4f46e5', '#222035', '#059669', '#dc2626', '#0284c7', '#7c3aed', '#ea580c', '#000000'];

export interface StyleConfigValues {
  primaryColor: string;
  position: string;
  title: string;
  greeting: string;
  otpGreeting: string;
  language: string;
  sessionTimeout: number;
}

interface Props {
  config: StyleConfigValues;
  onChange: (partial: Partial<StyleConfigValues>) => void;
  id: string | undefined;
  serverUrl: string;
  addLog: (type: string, msg: string, detail?: unknown) => void;
  widgetRef: RefObject<HTMLElement | null>;
}

export default function StyleConfigTab({ config, onChange, id, serverUrl, addLog, widgetRef }: Props) {
  const [isWidgetExpanded, setIsWidgetExpanded] = useState(false);

  const handleWidgetEvent = useCallback((type: string, detail: unknown) => {
    const shortName = type.replace('mcp-chat:', '');
    if (type === 'mcp-chat:expand') {
      setIsWidgetExpanded(true);
    } else if (type === 'mcp-chat:collapse') {
      setIsWidgetExpanded(false);
    } else if (type === 'mcp-chat:error') {
      const d = detail as Record<string, string> | null;
      addLog('error', `${shortName}: ${d?.technicalDetail || d?.error || 'Unknown error'}`);
    } else {
      addLog('event', shortName, detail);
    }
  }, [addLog]);

  return (
    <div className={`${styles.grid} ${isWidgetExpanded ? styles.gridExpanded : ''}`}>
      <div className={styles.left}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Primary Color</h3>
          <div className={styles.swatches}>
            {COLORS.map(c => (
              <div
                key={c}
                className={`${styles.swatch} ${config.primaryColor === c ? styles.swatchActive : ''}`}
                style={{ background: c }}
                onClick={() => onChange({ primaryColor: c })}
              />
            ))}
          </div>
          <div className={styles.field} style={{ marginTop: 12 }}>
            <label>Custom hex</label>
            <input
              type="text"
              value={config.primaryColor}
              onChange={e => onChange({ primaryColor: e.target.value })}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Layout</h3>
          <div className={styles.field}>
            <label>Position</label>
            <select value={config.position} onChange={e => onChange({ position: e.target.value })}>
              <option value="bottom-right">Bottom Right</option>
              <option value="bottom-left">Bottom Left</option>
            </select>
          </div>
        </div>
      </div>
      {/* Right: Live Preview */}
      <div className={styles.previewArea}>
        <span className={styles.previewLabel}>Preview</span>
        <WebchatWidget
          ref={widgetRef}
          serverUrl={serverUrl}
          auth="none"
          preview
          widgetId={id}
          primaryColor={config.primaryColor}
          position={config.position}
          language={config.language}
          title={config.title && (!WIDGET_LANGUAGE_DEFAULTS[config.language] || config.title !== WIDGET_LANGUAGE_DEFAULTS[config.language].title) ? config.title : undefined}
          greeting={config.greeting && (!WIDGET_LANGUAGE_DEFAULTS[config.language] || config.greeting !== WIDGET_LANGUAGE_DEFAULTS[config.language].greeting) ? config.greeting : undefined}
          otpGreeting={config.otpGreeting || undefined}
          sessionTimeout={config.sessionTimeout}
          onEvent={handleWidgetEvent}
          style={{ '--mcp-panel-height': '460px', '--mcp-panel-max-height': '460px' } as React.CSSProperties}
        />
      </div>
    </div>
  );
}
